import React, { FC, useCallback, useContext, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Optional } from 'utility-types'
import { Sorting } from '@devexpress/dx-react-grid'
import { useRequestOwnedMerch, useRequestUpdateMerch, useRequestUploadImages } from 'out-game/hooks/useRequest'
import { Merch } from 'out-game/types'
import { Routes } from 'out-game/constants'
import { calculatePaginationLength, getSortString } from 'out-game/utils'
import { TitleMerchSearchResult, Typography } from 'out-game/components'
import Window, { WindowProps } from 'out-game/components/Window'
import MerchFilter, {
  Value as MerchFilterValue,
} from 'out-game/containers/MerchFilter'
import * as Ctx from './context'
import './style.scss'
import InventoryDisplayCase from '../User/Inventory/InventoryTable'
import { InventoryTableColumns } from '../User/Inventory/InventoryTable/util'
import ButtonRestyled from '../../components/ButtonRestyled'
import { useAction, useTranslationStrict } from 'out-game/hooks'
import { WindowMerch } from 'out-game/containers'
import { useSelector } from 'react-redux'
import * as actions from 'out-game/actions/ui/merchPage'

export type WindowDealProps = Optional<
  WindowProps,
  'title' | 'open' | 'onClose'
>

const tableColumns = [
  InventoryTableColumns.name,
  InventoryTableColumns.description,
  InventoryTableColumns.amount,
  InventoryTableColumns.deal,
]

const ITEMS_PER_PAGE = 8

const WindowDeal: FC<WindowDealProps> = (props) => {
  const { t } = useTranslationStrict()

  const history = useHistory()
  const open = useContext(Ctx.IsOpen)
  const setOpen = useContext(Ctx.SetOpen)
  const [filterParams, setFilterParams] = useState<MerchFilterValue>(null)
  const [itemsCount, setItemsCount] = useState(0)
  const [items, setItems] = useState<Merch[]>([])

  const [pagesCount, setPagesCount] = useState(0)
  const [page, setPage] = useState<number>(0)
  const [pagesLoadedCount, setPagesLoadedCount] = useState<number>(0)

  const [sort, setSort] = useState<Sorting>({
    columnName: 'date_edited',
    direction: 'desc',
  })

  const [currentMerch, setCurrentMerch] = useState<Merch>()
  const isEditorOpen = useSelector(
    (state) => state.ui.merchPage.merchEditorOpen
  )
  const setIsEditorOpen = useAction(actions.setMerchEditorOpen)
  const [requestUpdate] = useRequestUpdateMerch()
  const [requestUploadImages] = useRequestUploadImages()

  const [requestOwnedMerch, ownedMerchRequestPending] = useRequestOwnedMerch()
  const { onClose, ...restProps } = props

  const getMerchList = (isAdditionalLoading = false) => {
    if (isAdditionalLoading && pagesLoadedCount < 1) return

    const params: { [key: string]: number | string | (number | string)[] } = {
      for_sale: 0 as 0 | 1 | 'all',
      offset:
        ITEMS_PER_PAGE * (isAdditionalLoading ? page + pagesLoadedCount : page),
      count: ITEMS_PER_PAGE,
    }

    if (sort) {
      const sortString = getSortString(sort)

      if (!sortString.includes('deal')) {
        params.filter = sortString
      }
    }

    if (filterParams) {
      const { games, name, ...restParams } = filterParams

      if (name) params.query = name
      if (games) params.games_ids = games.map((i) => i.id)

      for (let [key, value] of Object.entries(restParams)) {
        if (value) {
          params.item_type_slugs = [...(params.item_type_slugs || []), key]
        }
      }
    }

    requestOwnedMerch(params).then(({ data, count }) => {
      if (isAdditionalLoading) {
        setItems((prev) => [...prev, ...data])
      } else {
        setItemsCount(count || 0)
        setPagesCount(calculatePaginationLength(count || 0, ITEMS_PER_PAGE))
        setItems(data)
      }
    })
  }

  const onSaleClick = useCallback(() => {
    if (items.length === 1) {
      setPage((p) => p > 0 ? p - 1 : 0);
    } else {
      setPagesLoadedCount(0)
    }
    getMerchList()
  }, [items])

  const onDealButtonClick = (id: number) => {
    setCurrentMerch(items.find((item) => item.id === id))
  }

  useEffect(() => {
    if (open) {
      setPagesLoadedCount(0)
      setItems([])
      getMerchList()
    }
  }, [filterParams, page, sort, open])

  useEffect(() => {
    if (pagesLoadedCount > 0) {
      getMerchList(true)
    }
  }, [pagesLoadedCount])

  useEffect(() => {
    if (open) {
      return
    }
    setFilterParams(null)
    onClose && onClose()
  }, [open])

  return (
    <Window
      className="window-deal-modal"
      title={t('createDeal')}
      open={open}
      onClose={setOpen}
      {...restProps}
    >
      <div className="window-deal">
        <MerchFilter onChange={(params) => {
          setPage(0)
          setFilterParams(params)
        }} withSearch />
        {filterParams && (
          <TitleMerchSearchResult
            className="window-deal__full-width"
            amount={itemsCount}
          />
        )}
        {!ownedMerchRequestPending && !itemsCount && (
          <div className="window-deal__no-items">
            <Typography color="Emphasized" size={22}>
              {t('personal:inventory.errors.notTradingItems')}
            </Typography>
            <ButtonRestyled
              color="Secondary"
              onClick={() => {
                setOpen(false)
                history.push(Routes.UserInventory)
              }}
            >
              {t('personal:inventory.goToInventory')}
            </ButtonRestyled>
          </div>
        )}
        <InventoryDisplayCase
          items={items}
          page={page}
          pending={ownedMerchRequestPending}
          paginationLength={pagesCount}
          displayColumns={tableColumns}
          onPageChange={setPage}
          pagesLoadedCount={pagesLoadedCount}
          setPagesLoadedCount={setPagesLoadedCount}
          onMerchPublish={onSaleClick}
          onSortChange={setSort}
          onDealButtonClick={onDealButtonClick}
        />
        <WindowMerch
          // TODO: translate
          title="Редактировать товар:"
          submitButtonText={t('edit')}
          merch={currentMerch}
          open={!!currentMerch && isEditorOpen}
          onClose={setIsEditorOpen}
          onSubmit={async (value) => {
            if (currentMerch) {
              try {
                const response = await requestUpdate({
                  item_id: currentMerch.id,
                  name: value.name,
                  description: value.description,
                  options: value.options,
                  id_category: value.subCategory.id,
                  amount: value.amount,
                })
                if (response.error) {
                  console.error(response)
                  return
                }
                for (const formImage of value.images.filter(
                  (image) => image.item instanceof File
                )) {
                  const fd = new FormData()

                  fd.append('item_id', response.data.item_id.toString())
                  fd.append('file', formImage.item)

                  await requestUploadImages(fd)
                }
                getMerchList()
              } catch (e) {
                console.error(e)
              }
            }
          }}
          editor
        />
      </div>
    </Window>
  )
}

export default WindowDeal
